<template>
  <div class="position-relative table-schedule-draggable">
    <CfsVirtualTable
      ref="draggableTableRef"
      v-model="computedSelectedItems"
      :table-name="tableName"
      :loading="loading"
      :hide-intersection-loader="loading || hideIntersection"
      :headers="headers"
      :items="items"
      item-key="_id"
      :items-per-page="itemsPerPage"
      :height="height"
      :show-select="showSelect"
      v-bind="$attrs"
      v-on="$listeners"
    >
      <template v-if="!showSelectAll" #header.data-table-select></template>

      <template v-for="(_, name) in $scopedSlots" #[name]="slotData">
        <slot :name="name" v-bind="slotData" />
      </template>
    </CfsVirtualTable>
    <CfsVirtualTable
      ref="draggableTableImageRef"
      v-model="computedSelectedItems"
      :table-name="tableName"
      class="table-schedule-draggable_hidden"
      item-key="_id"
      :items="computedSelectedItems"
      hide-default-header
      hide-intersection-loader
      :show-select="showSelect"
      :show-total="false"
      :headers="headers"
    >
      <template v-for="(_, name) in $scopedSlots" #[name]="slotData">
        <slot :name="name" v-bind="slotData" />
      </template>
    </CfsVirtualTable>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, onBeforeUnmount, PropType, ref, watch } from 'vue';
import { CfsTableHeaders } from '@/store/modules/components/useCfsTableStore';

export default defineComponent({
  name: 'CfsVirtualDragTable',
  props: {
    tableName: {
      type: String,
      required: true,
      default: '',
    },
    value: {
      type: Array,
      default: () => [],
    },
    headers: {
      type: Array as PropType<CfsTableHeaders[]>,
      default: () => [],
    },
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    hideIntersection: {
      type: Boolean,
      default: false,
    },
    height: {
      type: String,
      default: '',
    },
    showSelect: {
      type: Boolean,
      default: false,
    },
    showSelectAll: {
      type: Boolean,
      default: false,
    },
    itemsPerPage: {
      type: Number,
      default: -1,
    },
  },
  emits: ['update:value', 'drag-start'],
  setup(props, { emit }) {
    const computedSelectedItems = computed({
      get() {
        return props.value;
      },
      set(items) {
        emit('update:value', items);
      },
    });

    const draggableTableRef = ref();
    const draggableTableImageRef = ref();

    const setTableTrDraggable = async () => {
      await nextTick();

      if (!draggableTableRef.value || !props.items?.length) return;

      draggableTableRef.value.$el.querySelectorAll('tbody > tr').forEach((tr: HTMLElement) => {
        tr.setAttribute('draggable', 'true');
        tr.addEventListener('dragstart', onDragStart);
      });
    };
    const deleteTableTrDraggableEvent = () => {
      draggableTableRef.value.$el.querySelectorAll('tbody > tr').forEach((tr: HTMLElement) => {
        tr.removeEventListener('dragstart', onDragStart);
      });
    };
    watch(() => props.items, setTableTrDraggable, { immediate: true, deep: true });
    onBeforeUnmount(deleteTableTrDraggableEvent);

    const onDragStart = (event: DragEvent) => {
      if (!props.value.length) {
        event.preventDefault();
        return;
      }
      if (!event.dataTransfer || !draggableTableImageRef.value) return;
      event.dataTransfer.setDragImage(draggableTableImageRef.value.$el, 0, 0);
      event.dataTransfer.dropEffect = 'copy';
      event.dataTransfer.effectAllowed = 'copy';

      emit('drag-start', event);
    };

    return {
      draggableTableRef,
      computedSelectedItems,
      draggableTableImageRef,
      onDragStart,
    };
  },
});
</script>

<style lang="scss">
.table-schedule-draggable {
  & .theme--light.v-data-table {
    background-color: #fdfcfa !important;
    border: 1px solid rgba(190, 145, 80, 0.17);
    box-shadow: 0 0 4px rgba(235, 235, 235, 0.25);
    border-radius: 5px;

    &.v-data-table--fixed-header thead th {
      background-color: #fdfcfa;
    }
  }

  :deep(.v-simple-checkbox) {
    .theme--light.v-icon.v-icon.v-icon--disabled {
      color: rgba(0, 0, 0, 0.1) !important;
    }
  }

  &_hidden {
    position: absolute;
    top: 0;
    z-index: -1;
    width: 100%;
  }

  :deep(.v-data-table__selected) {
    @apply cursor-move;
  }
}
</style>
